import React, { useEffect } from "react"

import { FileUploadButtonAlt } from "@components/Button"
import { SubmitSolid } from "@components/Button/SubmitSolid"
/*global gtag*/
import { useFormSettingsAlt } from "./useFormSettingsAlt"
import { useUploads } from "./useUploads"

const eventPixel = () => {
  const isBrowser = typeof window !== "undefined"
  if (!isBrowser) {
    return
  }
  // gtag("event", "contact_us_form_submission", { event_category: "contact_us", event_label: "submit" })
}

export const ESGEvalForm = ({
  id = "272",
  subject = "General Inquiry",
  recipient = "katiek@jkdesign.com, markm@jkdesign.com",
  fn = eventPixel,
}) => {
  const { register, formState, handleSubmit, onSubmit, formSubmitted, setValue } = useFormSettingsAlt({
    id,
    subject,
    recipient,
    fn: () => {
      eventPixel()
      fn()
    },
  })
  const {
    files,
    handleFileChange: uploadFileChange,
    removeFile,
  } = useUploads({
    name: "materials",
    setValue,
    maxFiles: 2,
  })
  const { errors } = formState

  useEffect(() => {
    register("materials", { required: false })
  }, [register])

  useEffect(() => {
    if (formSubmitted) {
      removeFile()
    }
  }, [formSubmitted]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileChange = (e) => {
    e.preventDefault()
    e.stopPropagation()
    uploadFileChange(e)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {/* Field 1: Maturity Level */}
      <div className="pt-8 pb-12 mb-6 rounded-md pl-7 pr-7 sm:pl-8 sm:pr-15" style={{ border: "1px solid #B6ACA3" }}>
        <div className="flex flex-col mb-4 sm:flex-row">
          <span className="mb-2 mr-2 font-medium text-20 sm:mb-0">1.</span>
          <div>
            <label htmlFor="maturity" className="font-medium text-20">
              How would you describe the current maturity of your sustainability efforts?
              <span style={{ color: "#F00" }}>*</span>
            </label>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:pl-8 gap-x-4 sm:grid-cols-2 lg:grid-cols-3 gap-y-5 sm:gap-y-10 mt-7">
          {["Advanced", "Mature", "Developing", "Early-stage"].map((option) => (
            <div key={option} className="flex items-center">
              <input
                type="radio"
                id={`maturity-${option.toLowerCase()}`}
                name="maturity"
                value={option}
                {...register("maturity", { required: true })}
                className="custom-radio text-20"
              />
              <label htmlFor={`maturity-${option.toLowerCase()}`} className="ml-3 lg:max-w-37">
                {option}
              </label>
            </div>
          ))}
        </div>
        {errors.maturity && (
          <p className="pt-2 pl-8 mt-1 text-14" style={{ color: "#F00" }}>
            This field is required
          </p>
        )}
      </div>

      {/* Field 2: Progress Description */}
      <div className="pt-8 pb-12 mb-6 rounded-md pl-7 pr-7 sm:pl-8 sm:pr-15" style={{ border: "1px solid #B6ACA3" }}>
        <div className="flex flex-col mb-4 sm:flex-row">
          <span className="mb-2 mr-2 font-medium text-20 sm:mb-0">2.</span>
          <div>
            <label htmlFor="progress" className="font-medium text-20">
              In your own words, how would you describe your current level of progress? What milestones or initiatives
              stand out so far?
              <span style={{ color: "#F00" }}>*</span>
            </label>
          </div>
        </div>
        <div className="sm:pl-8 mt-7">
          <input
            type="text"
            id="progress"
            {...register("progress", { required: true })}
            placeholder="Your answer"
            className="w-full p-2 bg-transparent"
            style={{
              border: "none",
              borderBottom: "2px solid #B6ACA3",
              borderRadius: "0",
              outline: "none",
            }}
          />
        </div>
        {errors.progress && (
          <p className="pt-2 pl-8 mt-1 text-14" style={{ color: "#F00" }}>
            This field is required
          </p>
        )}
      </div>

      {/* Field 3: Impact Level */}
      <div className="pt-8 pb-12 mb-6 rounded-md pl-7 pr-7 sm:pl-8 sm:pr-15" style={{ border: "1px solid #B6ACA3" }}>
        <div className="flex flex-col mb-4 sm:flex-row">
          <span className="mb-2 mr-2 font-medium text-20 sm:mb-0">3.</span>
          <div>
            <label htmlFor="impact" className="font-medium text-20">
              How would you describe the current impact of your sustainability efforts?
              <span style={{ color: "#F00" }}>*</span>
            </label>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:pl-8 gap-x-4 sm:grid-cols-2 lg:grid-cols-3 gap-y-5 sm:gap-y-10 mt-7">
          {["High", "Moderate", "Limited", "Too early to tell"].map((option) => (
            <div key={option} className="flex items-center">
              <input
                type="radio"
                id={`impact-${option.toLowerCase().replace(/\s+/g, "-")}`}
                name="impact"
                value={option}
                {...register("impact", { required: true })}
                className="custom-radio text-20"
              />
              <label htmlFor={`impact-${option.toLowerCase().replace(/\s+/g, "-")}`} className="ml-3 lg:max-w-37">
                {option}
              </label>
            </div>
          ))}
        </div>
        {errors.impact && (
          <p className="pt-2 pl-8 mt-1 text-14" style={{ color: "#F00" }}>
            This field is required
          </p>
        )}
      </div>

      {/* Field 4: Results Description */}
      <div className="pt-8 pb-12 mb-6 rounded-md pl-7 pr-7 sm:pl-8 sm:pr-15" style={{ border: "1px solid #B6ACA3" }}>
        <div className="flex flex-col mb-4 sm:flex-row">
          <span className="mb-2 mr-2 font-medium text-20 sm:mb-0">4.</span>
          <div>
            <label htmlFor="results" className="font-medium text-20">
              Tell us more about the results—or momentum—you're seeing. What impact do you hope to show in the near
              future?
              <span style={{ color: "#F00" }}>*</span>
            </label>
          </div>
        </div>
        <div className="sm:pl-8 mt-7">
          <input
            type="text"
            id="results"
            {...register("results", { required: true })}
            placeholder="Your answer"
            className="w-full p-2 bg-transparent"
            style={{
              border: "none",
              borderBottom: "2px solid #B6ACA3",
              borderRadius: "0",
              outline: "none",
            }}
          />
        </div>
        {errors.results && (
          <p className="pt-2 pl-8 mt-1 text-14" style={{ color: "#F00" }}>
            This field is required
          </p>
        )}
      </div>

      {/* Field 5: Leadership Commitment */}
      <div className="pt-8 pb-12 mb-6 rounded-md pl-7 pr-7 sm:pl-8 sm:pr-15" style={{ border: "1px solid #B6ACA3" }}>
        <div className="flex flex-col mb-4 sm:flex-row">
          <span className="mb-2 mr-2 font-medium text-20 sm:mb-0">5.</span>
          <div>
            <label htmlFor="leadership" className="font-medium text-20">
              How would you describe your leadership's commitment to sustainability?
              <span style={{ color: "#F00" }}>*</span>
            </label>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:pl-8 gap-x-4 sm:grid-cols-2 lg:grid-cols-3 gap-y-5 sm:gap-y-10 mt-7">
          {["Deeply committed", "Generally committed", "Somewhat supportive", "Hesitant"].map((option) => (
            <div key={option} className="flex items-center">
              <input
                type="radio"
                id={`leadership-${option.toLowerCase().replace(/\s+/g, "-")}`}
                name="leadership"
                value={option}
                {...register("leadership", { required: true })}
                className="custom-radio text-20"
              />
              <label htmlFor={`leadership-${option.toLowerCase().replace(/\s+/g, "-")}`} className="ml-3 lg:max-w-37">
                {option}
              </label>
            </div>
          ))}
        </div>
        {errors.leadership && (
          <p className="pt-2 pl-8 mt-1 text-14" style={{ color: "#F00" }}>
            This field is required
          </p>
        )}
      </div>

      {/* Field 6: Leadership Commitment Description */}
      <div className="pt-8 pb-12 mb-6 rounded-md pl-7 pr-7 sm:pl-8 sm:pr-15" style={{ border: "1px solid #B6ACA3" }}>
        <div className="flex flex-col mb-4 sm:flex-row">
          <span className="mb-2 mr-2 font-medium text-20 sm:mb-0">6.</span>
          <div>
            <label htmlFor="leadershipDescription" className="font-medium text-20">
              How would you describe leadership's commitment in your own words? Is sustainability a clear priority—and
              do your internal stakeholders seem aligned?
              <span style={{ color: "#F00" }}>*</span>
            </label>
          </div>
        </div>
        <div className="sm:pl-8 mt-7">
          <input
            type="text"
            id="leadershipDescription"
            {...register("leadershipDescription", { required: true })}
            placeholder="Your answer"
            className="w-full p-2 bg-transparent"
            style={{
              border: "none",
              borderBottom: "2px solid #B6ACA3",
              borderRadius: "0",
              outline: "none",
            }}
          />
        </div>
        {errors.leadershipDescription && (
          <p className="pt-2 pl-8 mt-1 text-14" style={{ color: "#F00" }}>
            This field is required
          </p>
        )}
      </div>

      {/* Field 7: Customer Attitude */}
      <div className="pt-8 pb-12 mb-6 rounded-md pl-7 pr-7 sm:pl-8 sm:pr-15" style={{ border: "1px solid #B6ACA3" }}>
        <div className="flex flex-col mb-4 sm:flex-row">
          <span className="mb-2 mr-2 font-medium text-20 sm:mb-0">7.</span>
          <div>
            <label htmlFor="customerAttitude" className="font-medium text-20">
              How would you describe your customers' attitude toward sustainability?
              <span style={{ color: "#F00" }}>*</span>
            </label>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:pl-8 gap-x-4 sm:grid-cols-2 lg:grid-cols-3 gap-y-5 sm:gap-y-10 mt-7">
          {["Very positive", "Mixed", "Neutral", "Resistant"].map((option) => (
            <div key={option} className="flex items-center">
              <input
                type="radio"
                id={`customer-${option.toLowerCase().replace(/\s+/g, "-")}`}
                name="customerAttitude"
                value={option}
                {...register("customerAttitude", { required: true })}
                className="custom-radio text-20"
              />
              <label htmlFor={`customer-${option.toLowerCase().replace(/\s+/g, "-")}`} className="ml-3 lg:max-w-37">
                {option}
              </label>
            </div>
          ))}
        </div>
        {errors.customerAttitude && (
          <p className="pt-2 pl-8 mt-1 text-14" style={{ color: "#F00" }}>
            This field is required
          </p>
        )}
      </div>

      {/* Field 8: Customer Signals */}
      <div className="pt-8 pb-12 mb-6 rounded-md pl-7 pr-7 sm:pl-8 sm:pr-15" style={{ border: "1px solid #B6ACA3" }}>
        <div className="flex flex-col mb-4 sm:flex-row">
          <span className="mb-2 mr-2 font-medium text-20 sm:mb-0">8.</span>
          <div>
            <label htmlFor="customerSignals" className="font-medium text-20">
              What signals have you received from your customers, employees, or partners—either directly or
              indirectly—about how they view sustainability?
              <span style={{ color: "#F00" }}>*</span>
            </label>
          </div>
        </div>
        <div className="sm:pl-8 mt-7">
          <input
            type="text"
            id="customerSignals"
            {...register("customerSignals", { required: true })}
            placeholder="Your answer"
            className="w-full p-2 bg-transparent"
            style={{
              border: "none",
              borderBottom: "2px solid #B6ACA3",
              borderRadius: "0",
              outline: "none",
            }}
          />
        </div>
        {errors.customerSignals && (
          <p className="pt-2 pl-8 mt-1 text-14" style={{ color: "#F00" }}>
            This field is required
          </p>
        )}
      </div>

      {/* Field 9: Brand Visibility */}
      <div className="pt-8 pb-12 mb-6 rounded-md pl-7 pr-7 sm:pl-8 sm:pr-15" style={{ border: "1px solid #B6ACA3" }}>
        <div className="flex flex-col mb-4 sm:flex-row">
          <span className="mb-2 mr-2 font-medium text-20 sm:mb-0">9.</span>
          <div>
            <label htmlFor="brandVisibility" className="font-medium text-20">
              How visible or bold is your brand when communicating about sustainability?
              <span style={{ color: "#F00" }}>*</span>
            </label>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:pl-8 gap-x-4 sm:grid-cols-2 lg:grid-cols-3 gap-y-5 sm:gap-y-10 mt-7">
          {["Very visible", "Somewhat visible", "Quiet", "Low-profile", "Other"].map((option) => (
            <div key={option} className="flex items-center gap-2">
              <input
                type="radio"
                id={`visibility-${option.toLowerCase().replace(/\s+/g, "-")}`}
                name="brandVisibility"
                value={option}
                {...register("brandVisibility", { required: true })}
                className="custom-radio text-20"
                style={{ minWidth: 32, minHeight: 32, width: 32, height: 32 }}
              />
              <label
                htmlFor={`visibility-${option.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex-1 ml-3 text-left"
                style={{ whiteSpace: "normal" }}
              >
                {option}
              </label>
            </div>
          ))}
        </div>
        {errors.brandVisibility && (
          <p className="pt-2 pl-8 mt-1 text-14" style={{ color: "#F00" }}>
            This field is required
          </p>
        )}
      </div>

      {/* Field 10: Brand Voice */}
      <div className="pt-8 pb-12 mb-6 rounded-md pl-7 pr-7 sm:pl-8 sm:pr-15" style={{ border: "1px solid #B6ACA3" }}>
        <div className="flex flex-col mb-4 sm:flex-row">
          <span className="mb-2 mr-2 font-medium text-20 sm:mb-0">10.</span>
          <div>
            <label htmlFor="brandVoice" className="font-medium text-20">
              In your own words, how would you describe your brand's voice when talking about sustainability? Is it
              confident? Careful? Earnest? Strategic?
              <span style={{ color: "#F00" }}>*</span>
            </label>
          </div>
        </div>
        <div className="sm:pl-8 mt-7">
          <input
            type="text"
            id="brandVoice"
            {...register("brandVoice", { required: true })}
            placeholder="Your answer"
            className="w-full p-2 bg-transparent"
            style={{
              border: "none",
              borderBottom: "2px solid #B6ACA3",
              borderRadius: "0",
              outline: "none",
            }}
          />
        </div>
        {errors.brandVoice && (
          <p className="pt-2 pl-8 mt-1 text-14" style={{ color: "#F00" }}>
            This field is required
          </p>
        )}
      </div>

      {/* Field 11: Sustainability Tone */}
      <div className="pt-8 pb-12 mb-6 rounded-md pl-7 pr-7 sm:pl-8 sm:pr-15" style={{ border: "1px solid #B6ACA3" }}>
        <div className="flex flex-col mb-4 sm:flex-row">
          <span className="mb-2 mr-2 font-medium text-20 sm:mb-0">11.</span>
          <div>
            <label htmlFor="sustainabilityTone" className="font-medium text-20">
              What tone best reflects how you want your sustainability story to come across?
              <span style={{ color: "#F00" }}>*</span>
            </label>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:pl-8 sm:grid-cols-2 lg:grid-cols-4 gap-y-5 sm:gap-y-10 mt-7">
          {["Pragmatic", "Neutral", "Inspiring", "Empowering", "Reassuring", "Serious", "Other"].map((option) => (
            <div key={option} className="flex items-center">
              <input
                type="radio"
                id={`tone-${option.toLowerCase().replace(/\s+/g, "-")}`}
                name="sustainabilityTone"
                value={option}
                {...register("sustainabilityTone", { required: true })}
                className="custom-radio text-20"
              />
              <label
                htmlFor={`tone-${option.toLowerCase().replace(/\s+/g, "-")}`}
                className="ml-3 lg:max-w-37 whitespace-nowrap"
              >
                {option}
              </label>
            </div>
          ))}
        </div>
        {errors.sustainabilityTone && (
          <p className="pt-2 pl-8 mt-1 text-14" style={{ color: "#F00" }}>
            This field is required
          </p>
        )}
      </div>

      {/* Field 12: Audience Tone */}
      <div className="pt-8 pb-12 mb-6 rounded-md pl-7 pr-7 sm:pl-8 sm:pr-15" style={{ border: "1px solid #B6ACA3" }}>
        <div className="flex flex-col mb-4 sm:flex-row">
          <span className="mb-2 mr-2 font-medium text-20 sm:mb-0">12.</span>
          <div>
            <label htmlFor="audienceTone" className="font-medium text-20">
              What kind of tone feels most appropriate for your audience? Are there sensitivities, brand values, or
              cultural cues you're navigating?
              <span style={{ color: "#F00" }}>*</span>
            </label>
          </div>
        </div>
        <div className="sm:pl-8 mt-7">
          <input
            type="text"
            id="audienceTone"
            {...register("audienceTone", { required: true })}
            placeholder="Your answer"
            className="w-full p-2 bg-transparent"
            style={{
              border: "none",
              borderBottom: "2px solid #B6ACA3",
              borderRadius: "0",
              outline: "none",
            }}
          />
        </div>
        {errors.audienceTone && (
          <p className="pt-2 pl-8 mt-1 text-14" style={{ color: "#F00" }}>
            This field is required
          </p>
        )}
      </div>

      {/* Field 13: File Upload */}
      <div className="pt-8 pb-12 mb-6 rounded-md pl-7 pr-7 sm:pl-8 sm:pr-15" style={{ border: "1px solid #B6ACA3" }}>
        <div className="flex flex-col mb-4 sm:flex-row">
          <span className="mb-2 mr-2 font-medium text-20 sm:mb-0">13.</span>
          <div>
            <label htmlFor="materials" className="font-medium text-20">
              Upload any materials you'd like us to review.
            </label>
            <span className="block mt-2 font-normal text-16">
              Examples: ESG reports, internal slides, program overviews. (Max. 2 uploads, 10 MB each)
            </span>
          </div>
        </div>
        <div className="flex sm:pl-8 mt-7">
          <FileUploadButtonAlt
            label="Add file"
            files={files}
            removeFile={removeFile}
            handleFileChange={handleFileChange}
          />
        </div>
      </div>

      {/* Field 14: Web Links */}
      <div className="pt-8 pb-12 mb-6 rounded-md pl-7 pr-7 sm:pl-8 sm:pr-15" style={{ border: "1px solid #B6ACA3" }}>
        <div className="flex flex-col mb-4 sm:flex-row">
          <span className="mb-2 mr-2 font-medium text-20 sm:mb-0">14.</span>
          <div>
            <label htmlFor="webLinks" className="font-medium text-20">
              Share any web links that help show your sustainability story in action.
            </label>
            <span className="block mt-2 font-normal text-16">
              Examples: ESG site, About page, press releases, leader social posts, investor messaging.
            </span>
          </div>
        </div>
        <div className="sm:pl-8 mt-7">
          <input
            type="text"
            id="webLinks"
            {...register("webLinks")}
            placeholder="URL(S)"
            className="w-full p-2 bg-transparent"
            style={{
              border: "none",
              borderBottom: "2px solid #B6ACA3",
              borderRadius: "0",
              outline: "none",
            }}
          />
        </div>
      </div>

      {/* Field 15: Contact Information */}
      <div className="pt-8 pb-12 mb-6 rounded-md pl-7 pr-7 sm:pl-8 sm:pr-15" style={{ border: "1px solid #B6ACA3" }}>
        <div className="flex flex-col mb-4 sm:flex-row">
          <span className="mb-2 mr-2 font-medium text-20 sm:mb-0">15.</span>
          <div>
            <label htmlFor="contactName" className="font-medium text-20">
              Who should we send your evaluation to?
              <span style={{ color: "#F00" }}>*</span>
            </label>
            <span className="block mt-2 font-normal text-16">
              Please include the best contact for delivery and follow-up questions.
            </span>
          </div>
        </div>
        <div className="sm:pl-8 mt-7">
          <div className="grid grid-cols-2 gap-x-6 gap-y-6">
            <div>
              <input
                type="text"
                id="contactName"
                {...register("contactName", { required: true })}
                placeholder="Name"
                className="w-full p-2 bg-transparent"
                style={{
                  border: "none",
                  borderBottom: "2px solid #B6ACA3",
                  borderRadius: "0",
                  outline: "none",
                }}
              />
              {errors.contactName && (
                <p className="pt-2 mt-1 text-14" style={{ color: "#F00" }}>
                  This field is required
                </p>
              )}
            </div>
            <div>
              <input
                type="text"
                id="contactCompany"
                {...register("contactCompany", { required: true })}
                placeholder="Company"
                className="w-full p-2 bg-transparent"
                style={{
                  border: "none",
                  borderBottom: "2px solid #B6ACA3",
                  borderRadius: "0",
                  outline: "none",
                }}
              />
              {errors.contactCompany && (
                <p className="pt-2 mt-1 text-14" style={{ color: "#F00" }}>
                  This field is required
                </p>
              )}
            </div>
            <div>
              <input
                type="email"
                id="contactEmail"
                {...register("contactEmail", {
                  required: true,
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Please enter a valid email address",
                  },
                })}
                placeholder="Email"
                className="w-full p-2 bg-transparent"
                style={{
                  border: "none",
                  borderBottom: "2px solid #B6ACA3",
                  borderRadius: "0",
                  outline: "none",
                }}
              />
              {errors.contactEmail && (
                <p className="pt-2 mt-1 text-14" style={{ color: "#F00" }}>
                  {errors.contactEmail.message || "This field is required"}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex mt-12">
        <div className="flex flex-col justify-between lg:flex-row lg:gap-x-20">
          <div className="w-full sm:max-w-[706px]">
            <p className="text-20">
              Once we receive your responses, we'll get to work. Expect a custom roadmap delivered to your inbox within
              5–10 business days. If anything's unclear, we'll reach out.
            </p>
          </div>

          <div className="flex-shrink-0 mt-7 lg:mt-0">
            <SubmitSolid
              label="Submit"
              formSubmitted={formSubmitted}
              disabled={formState.isSubmitting}
              className="text-white bg-black border-black"
            />
          </div>
        </div>
      </div>
      {formSubmitted && (
        <div className="pt-clamp-8-20">
          <p>Thank you! Your message has been sent. Someone will be in touch shortly.</p>
        </div>
      )}
    </form>
  )
}
